import { useEffect, useState } from 'react';
import api from '../../service/apiService';
import DashboardHeader from '../Dashboard/DashboardHeader/DashboardHeader';
import { useParams, useNavigate } from 'react-router-dom';
import './CaseView.css';

export default function CaseView({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingDocuments, setIsRefreshingDocuments] = useState(false);

  // Admin editing states
  const [isEditing, setIsEditing] = useState(false);
  const [newDocuments, setNewDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Force non-admin UI for client-only app
  // Previously: const isAdmin = user?.roles?.includes('admin') || user?.user_type === 'admin';
  const isAdmin = false;

  useEffect(() => {
    const fetchCase = async () => {
      try {
        // Use the API service function for a single case
        const data = await api.getCaseById(id);

        // Backend now returns a single object instead of an array
        setCaseData(data || null);
      } catch (err) {
        console.error('Error fetching case:', err);

        // If it's a 403 or 401, redirect to login
        if (
          err.message.includes('403') ||
          err.message.includes('401') ||
          err.message.includes('Unauthorized')
        ) {
          localStorage.removeItem('jwtToken');
          localStorage.removeItem('userInfo');
          navigate('/login');
          return;
        }

        setCaseData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCase();
  }, [id, user, navigate]);

  // Auto-hide inline toast messages
  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(''), 4000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return '#f39c12';
      case 'in_progress':
        return '#3498db';
      case 'completed':
        return '#27ae60';
      case 'rejected':
        return '#e74c3c';
      default:
        return '#95a5a6';
    }
  };

  const getCaseTypeLabel = (type) => {
    switch (type) {
      case 'law_inquiry':
        return 'Law Inquiry';
      case 'will_request':
        return 'Will Request';
      default:
        return 'Case';
    }
  };

  const handleDocumentClick = async (doc, index) => {
    // Check if URL might be expired (simple check - if it's more than 30 minutes old)
    const urlCreationTime = new Date(caseData.created_at).getTime();
    const now = new Date().getTime();
    const thirtyMinutes = 30 * 60 * 1000;

    if (now - urlCreationTime > thirtyMinutes) {
      setIsRefreshingDocuments(true);
      try {
        // Refresh the case data to get new presigned URLs
        const data = await api.getCaseById(id);
        const refreshedCase = data || null;
        setCaseData(refreshedCase);

        // Parse documents if they're in string format
        let documents = refreshedCase.documents;
        if (typeof documents === 'string') {
          documents = JSON.parse(documents);
        }

        // Open the refreshed document URL
        if (refreshedCase && documents && documents[index]) {
          window.open(documents[index].url, '_blank', 'noopener,noreferrer');
        }
      } catch (err) {
        console.error('Error refreshing document URLs:', err);
        alert('Unable to access document. Please try again.');
      } finally {
        setIsRefreshingDocuments(false);
      }
    } else {
      // URL should still be valid, open directly
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Admin functions
  const handleFileUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    setNewDocuments((prev) => [...prev, ...uploadedFiles]);
  };

  const removeNewDocument = (index) => {
    setNewDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadDocuments = async () => {
    if (newDocuments.length === 0) return;

    setIsUploading(true);
    try {
      // Pass documents correctly
      await api.updateCase(id, { documents: newDocuments });

      // Refresh case data to show new documents
      const updatedCase = await api.getCaseById(id);
      setCaseData(updatedCase);

      // Clear uploaded files
      setNewDocuments([]);
      alert('Documents uploaded successfully!');
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('Failed to upload documents. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCompleteCase = async () => {
    if (!window.confirm('Are you sure you want to mark this case as completed?')) return;

    setIsUpdatingStatus(true);
    try {
      // Attempt to charge the saved payment method for this case first
      // Temporary amount: $1 (100 cents)
      try {
        await api.createPaymentIntent({ caseId: id, amount: 100 });
      } catch (chargeErr) {
        console.error('Charge failed when completing case:', chargeErr);
        setToastMessage(
          'Charging the payment method failed. Case will not be marked completed.' +
            (chargeErr.message ? ' Error: ' + chargeErr.message : '')
        );
        // Refresh case data in case something changed
        const updatedCase = await api.getCaseById(id);
        setCaseData(updatedCase);
        return;
      }

      // If charge succeeded, mark the case completed
      await api.updateCase(id, { status: 'completed' });
      const updatedCase = await api.getCaseById(id);
      setCaseData(updatedCase);
      setToastMessage('Case marked as completed and charged successfully!');
      setIsEditing(false);
    } catch (error) {
      console.error('Error completing case:', error);
      alert('Failed to complete case. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleRejectCase = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await api.updateCase(id, {
        status: 'rejected',
        rejection_reason: rejectionReason.trim(),
      });

      // Refresh case data
      const updatedCase = await api.getCaseById(id);
      setCaseData(updatedCase);

      alert('Case rejected successfully!');
      setShowRejectionModal(false);
      setRejectionReason('');
      setIsEditing(false);
    } catch (error) {
      console.error('Error rejecting case:', error);
      alert('Failed to reject case. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStartProgress = async () => {
    if (!window.confirm('Mark this case as in progress?')) return;

    setIsUpdatingStatus(true);
    try {
      await api.updateCase(id, { status: 'in_progress' });

      // Refresh case data
      const updatedCase = await api.getCaseById(id);
      setCaseData(updatedCase);

      alert('Case marked as in progress!');
    } catch (error) {
      console.error('Error updating case status:', error);
      alert('Failed to update case status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleConfirmCancel = async () => {
    // Confirmed cancellation by customer
    setIsCancelling(true);
    try {
      const res = await api.cancelCase(id);

      // Refresh case data
      const updatedCase = await api.getCaseById(id);
      setCaseData(updatedCase);

      if (res && res.charged) {
        const chargedAmount = res.chargeAmount != null ? (res.chargeAmount / 100).toFixed(2) : null;
        setToastMessage(
          `Case cancelled. A cancellation fee of ${chargedAmount ? '$' + chargedAmount : 'an amount'} was charged to your payment method.`
        );
      } else {
        setToastMessage(res.message || 'Case cancelled successfully.');
      }

      setShowCancelModal(false);
    } catch (err) {
      console.error('Error cancelling case:', err);
      // api.request throws Error with message containing server response
      alert('Failed to cancel case: ' + (err.message || JSON.stringify(err)));
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading)
    return (
      <>
        <DashboardHeader user={user} onLogout={onLogout} />
        <p className="loading-message">Loading case details...</p>
      </>
    );

  if (!caseData)
    return (
      <>
        <DashboardHeader user={user} onLogout={onLogout} />
        <p className="loading-message">Case not found.</p>
      </>
    );

  // Parse documents if they're in string format (from your backend)
  let documents = [];
  if (caseData.documents) {
    try {
      documents =
        typeof caseData.documents === 'string'
          ? JSON.parse(caseData.documents)
          : caseData.documents;
    } catch (e) {
      console.error('Error parsing documents:', e);
      documents = [];
    }
  }

  return (
    <>
      <DashboardHeader user={user} onLogout={onLogout} />
      {/* Overlay shown during cancelling to prevent interactions */}
      {isCancelling && <div className="page-overlay" />}
      {toastMessage && <div className="inline-toast">{toastMessage}</div>}
      <div className="case-view-container">
        <div className="case-card">
          <div className="case-header">
            <h2>{caseData.case_title}</h2>
            <div className="case-header-actions">
              <span
                className="case-status"
                style={{ backgroundColor: getStatusColor(caseData.progress_status) }}
              >
                {(caseData.progress_status || 'unknown').replace('_', ' ').toUpperCase()}
              </span>
              {isAdmin && (
                <button
                  className={`edit-toggle-btn ${isEditing ? 'active' : ''}`}
                  onClick={() => setIsEditing(!isEditing)}
                  disabled={isUpdatingStatus}
                >
                  {isEditing ? 'Cancel Edit' : 'Edit Case'}
                </button>
              )}
              {/* Customer Cancel Button (only for customers and for new/in_progress statuses) */}
              {!isAdmin &&
                (caseData.progress_status === 'new' ||
                  caseData.progress_status === 'in_progress') && (
                  <button
                    className="btn-danger"
                    onClick={() => setShowCancelModal(true)}
                    disabled={isCancelling}
                    style={{ marginLeft: 8 }}
                  >
                    {isCancelling ? 'Cancelling...' : 'Cancel Case'}
                  </button>
                )}
            </div>
          </div>

          <div className="case-details">
            <span>
              <strong>Type:</strong> {getCaseTypeLabel(caseData.case_type)}
            </span>
            <span>
              <strong>Urgency:</strong> {caseData.urgency_level}
            </span>
            <span>
              <strong>Client:</strong>{' '}
              {caseData.client_name || `${caseData.first_name} ${caseData.last_name}`}
            </span>
            <span>
              <strong>Email:</strong> {caseData.email}
            </span>
            <span>
              <strong>Submitted:</strong> {new Date(caseData.created_at).toLocaleDateString()}
            </span>
            <div className="case-description">
              <strong>Description:</strong>
              <p>{caseData.case_description}</p>
            </div>
            {caseData.rejection_reason && (
              <div className="rejection-reason">
                <strong>Rejection Reason:</strong>
                <p>{caseData.rejection_reason}</p>
              </div>
            )}
          </div>

          {documents && documents.length > 0 && (
            <div className="documents-list">
              <h4>Documents:</h4>
              {isRefreshingDocuments && <p>Refreshing document access...</p>}
              {documents.map((doc, index) => (
                <button
                  key={index}
                  className="document-link"
                  onClick={() => handleDocumentClick(doc, index)}
                  disabled={isRefreshingDocuments || !doc.url}
                >
                  {doc.name} {doc.error && <span style={{ color: 'red' }}>({doc.error})</span>}
                </button>
              ))}
            </div>
          )}

          {/* Admin editing section */}
          {isAdmin && isEditing && (
            <div className="admin-edit-section">
              <h4>Admin Actions</h4>

              {/* Document upload */}
              <div className="document-upload-section">
                <h5>Upload Additional Documents</h5>
                <input
                  type="file"
                  id="admin-file-upload"
                  multiple
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => document.getElementById('admin-file-upload').click()}
                  disabled={isUploading}
                >
                  Select Files
                </button>

                {newDocuments.length > 0 && (
                  <div className="new-documents-list">
                    <h6>Files to upload:</h6>
                    {newDocuments.map((file, index) => (
                      <div key={index} className="new-document-item">
                        <span>{file.name}</span>
                        <button
                          type="button"
                          className="remove-doc-btn"
                          onClick={() => removeNewDocument(index)}
                          disabled={isUploading}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      className="btn-primary"
                      onClick={handleUploadDocuments}
                      disabled={isUploading}
                    >
                      {isUploading ? 'Uploading...' : 'Upload Documents'}
                    </button>
                  </div>
                )}
              </div>

              {/* Status actions */}
              <div className="status-actions">
                <h5>Update Case Status</h5>
                <div className="status-buttons">
                  {caseData.progress_status === 'new' && (
                    <button
                      className="btn-info"
                      onClick={handleStartProgress}
                      disabled={isUpdatingStatus}
                    >
                      {isUpdatingStatus ? 'Updating...' : 'Start Progress'}
                    </button>
                  )}

                  {(caseData.progress_status === 'new' ||
                    caseData.progress_status === 'in_progress') && (
                    <>
                      <button
                        className="btn-success"
                        onClick={handleCompleteCase}
                        disabled={isUpdatingStatus}
                      >
                        {isUpdatingStatus ? 'Updating...' : 'Complete Case'}
                      </button>

                      <button
                        className="btn-danger"
                        onClick={() => setShowRejectionModal(true)}
                        disabled={isUpdatingStatus}
                      >
                        Reject Case
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <button className="back-button" onClick={() => navigate(-1)}>
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="modal-overlay" onClick={() => setShowRejectionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Case</h3>
              <button
                className="modal-close"
                onClick={() => setShowRejectionModal(false)}
                disabled={isUpdatingStatus}
              >
                ×
              </button>
            </div>
            <div className="modal"></div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="rejection-reason">Reason for Rejection *</label>
                <textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a detailed reason for rejecting this case..."
                  rows={4}
                  disabled={isUpdatingStatus}
                  required
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowRejectionModal(false)}
                disabled={isUpdatingStatus}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleRejectCase}
                disabled={isUpdatingStatus || !rejectionReason.trim()}
              >
                {isUpdatingStatus ? 'Rejecting...' : 'Reject Case'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Cancel Confirmation Modal (customer) */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Case</h3>
              <button
                className="modal-close"
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to cancel this case? If the case is{' '}
                <strong>in progress</strong>, cancelling will apply a charge of <strong>50%</strong>{' '}
                of the original price to the payment method on file.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
              >
                Back
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleConfirmCancel}
                disabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Auto-hide toast in this component
// (placed after export to avoid hook order issues; using a simple pattern would require moving this into the component, so instead we keep it inside the component via effect)
